/**
 * Media library page.
 *
 * Lists every video that belongs to the current admin (uploaded + directory)
 * and shows where each one currently lives:
 *
 *   • on the local server,
 *   • in the user's own S3 bucket,
 *   • or in both places at once (mirrored for fallback playback).
 *
 * From here the user can:
 *   • configure their AWS S3 bucket (bucket / region / access key / secret),
 *   • test the saved S3 credentials,
 *   • transfer a video from server -> S3 (after ffmpeg processing),
 *   • download a video from S3 back onto the server,
 *   • delete a video from server, S3, or both,
 *   • play the video using the preferred source (with automatic fallback).
 *
 * All state is fetched from the backend `/api/media/library/` and
 * `/api/s3/config/` endpoints – there is no client-side persistence beyond
 * the JWT in localStorage.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import routerPath from "../../utils/router/routerPath";

import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import DeleteIcon from "@mui/icons-material/Delete";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import RefreshIcon from "@mui/icons-material/Refresh";
import SettingsIcon from "@mui/icons-material/Settings";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";

import { toast } from "react-toastify";

import Layout from "../common/layout";
import {
  deleteS3Config,
  getMediaLibrary,
  getMediaPlaybackUrl,
  getS3Config,
  removeMedia,
  saveS3Config,
  testS3Config,
  transferMedia,
} from "../../utils/apis/apis";

// --- helpers ---------------------------------------------------------------

const formatBytes = (bytes) => {
  if (bytes === null || bytes === undefined) return "—";
  if (bytes === 0) return "0 B";
  const units = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.min(units.length - 1, Math.floor(Math.log(bytes) / Math.log(1024)));
  return `${(bytes / Math.pow(1024, i)).toFixed(i ? 1 : 0)} ${units[i]}`;
};

const locationChipColor = (loc) => {
  switch (loc) {
    case "both":
      return "success";
    case "s3":
      return "info";
    case "server":
      return "primary";
    case "missing":
    default:
      return "default";
  }
};

// --- S3 config dialog ------------------------------------------------------

function S3ConfigDialog({ open, onClose, token, current, onSaved }) {
  const [form, setForm] = useState({
    bucket_name: "",
    region_name: "us-east-1",
    access_key_id: "",
    secret_access_key: "",
    endpoint_url: "",
    prefix: "letswebinar",
  });
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  // When the dialog opens, prefill with whatever the server returned (the
  // secret is *never* sent back, so we leave it blank for the user to retype
  // only if they want to change it).
  useEffect(() => {
    if (!open) return;
    setForm({
      bucket_name: current?.bucket_name || "",
      region_name: current?.region_name || "us-east-1",
      access_key_id: current?.access_key_id || "",
      secret_access_key: "",
      endpoint_url: current?.endpoint_url || "",
      prefix: current?.prefix == null ? "letswebinar" : current.prefix,
    });
  }, [open, current]);

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSave = async () => {
    if (!form.bucket_name || !form.access_key_id) {
      toast.error("Bucket name and access key are required.");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form };
      // Don't send a blank secret – that signals "keep existing".
      if (!payload.secret_access_key) delete payload.secret_access_key;
      const res = await saveS3Config(token, payload);
      if (res.status === "success") {
        toast.success(res.message || "S3 configuration saved.");
        onSaved && onSaved();
        onClose && onClose();
      } else {
        toast.error(res.message || "Could not save S3 configuration.");
      }
    } catch (err) {
      toast.error(
        err?.response?.data?.message || "Could not save S3 configuration."
      );
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    try {
      const res = await testS3Config(token);
      if (res.status === "success") {
        toast.success(res.message || "S3 credentials are valid.");
      } else {
        toast.error(res.message || "S3 test failed.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "S3 test failed.");
    } finally {
      setTesting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>AWS S3 Bucket Configuration</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          Each user configures their own bucket. Credentials are stored
          encrypted on the server and are never exposed back to the browser.
        </DialogContentText>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={8}>
            <TextField
              label="Bucket name"
              fullWidth
              value={form.bucket_name}
              onChange={update("bucket_name")}
              required
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Region"
              fullWidth
              value={form.region_name}
              onChange={update("region_name")}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Access key ID"
              fullWidth
              value={form.access_key_id}
              onChange={update("access_key_id")}
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label={
                current?.has_secret
                  ? "Secret access key (leave blank to keep)"
                  : "Secret access key"
              }
              fullWidth
              type="password"
              value={form.secret_access_key}
              onChange={update("secret_access_key")}
              required={!current?.has_secret}
            />
          </Grid>
          <Grid item xs={12} sm={8}>
            <TextField
              label="Custom endpoint URL (optional)"
              placeholder="e.g. https://s3.us-west-2.amazonaws.com or DigitalOcean / Wasabi URL"
              fullWidth
              value={form.endpoint_url}
              onChange={update("endpoint_url")}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Folder prefix"
              fullWidth
              value={form.prefix}
              onChange={update("prefix")}
              helperText="Files land under <bucket>/<prefix>/…"
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        {current?.has_secret && (
          <Button onClick={handleTest} disabled={testing}>
            {testing ? "Testing…" : "Test connection"}
          </Button>
        )}
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" disabled={saving}>
          {saving ? "Saving…" : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// --- main page -------------------------------------------------------------

function MediaPage() {
  const sessionUser = useSelector((state) => state.session.user);
  const token = sessionUser?.access;
  const navigate = useNavigate();


  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [s3Configured, setS3Configured] = useState(false);
  const [s3Config, setS3Config] = useState(null);
  const [configOpen, setConfigOpen] = useState(false);
  // Per-row "in-flight" flag so the user can't fire two transfers at once.
  const [busyRow, setBusyRow] = useState(null);
  const [filter, setFilter] = useState("all");
  const [prefer, setPrefer] = useState("server");

  const refresh = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const [libraryRes, configRes] = await Promise.all([
        getMediaLibrary(token),
        getS3Config(token).catch(() => ({ configured: false, data: null })),
      ]);
      setItems(Array.isArray(libraryRes?.data) ? libraryRes.data : []);
      setS3Configured(!!libraryRes?.s3_configured);
      setS3Config(configRes?.data || null);
    } catch (err) {
      toast.error(
        err?.response?.data?.message || "Could not load the media library."
      );
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const filtered = useMemo(() => {
    if (filter === "all") return items;
    return items.filter((it) => it.storage_location === filter);
  }, [items, filter]);

  const rowKey = (it) => `${it.type}:${it.id}`;

  const onTransfer = async (it, direction) => {
    setBusyRow(rowKey(it));
    try {
      const res = await transferMedia(token, {
        id: it.id,
        type: it.type,
        direction,
      });
      if (res.status === "success") {
        toast.success(res.message || "Transfer complete.");
        await refresh();
      } else {
        toast.error(res.message || "Transfer failed.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Transfer failed.");
    } finally {
      setBusyRow(null);
    }
  };

  const onRemove = async (it, target) => {
    const niceTarget = target === "both" ? "both copies" : `the ${target} copy`;
    // eslint-disable-next-line no-restricted-globals
    if (!window.confirm(`Remove ${niceTarget} of "${it.name || it.filename}"?`)) {
      return;
    }
    setBusyRow(rowKey(it));
    try {
      const res = await removeMedia(token, {
        id: it.id,
        type: it.type,
        target,
      });
      if (res.status === "success") {
        toast.success(res.message || "Removed.");
        await refresh();
      } else {
        toast.error(res.message || "Removal failed.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Removal failed.");
    } finally {
      setBusyRow(null);
    }
  };

  const onPlay = async (it) => {
    try {
      const res = await getMediaPlaybackUrl(token, {
        id: it.id,
        type: it.type,
        prefer,
      });
      if (res.status === "success" && res.url) {
        // Open the URL in a new tab – a presigned S3 URL works directly with
        // the browser's native <video>. Server-side streams are also a
        // straight HTTP GET so the same approach works for both sources.
        window.open(res.url, "_blank", "noopener,noreferrer");
        toast.info(`Playing from ${res.source}.`);
      } else {
        toast.error(res.message || "No playable copy found.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Could not get a playback URL.");
    }
  };

  const onDeleteConfig = async () => {
    // eslint-disable-next-line no-restricted-globals
    if (!window.confirm("Remove your saved S3 credentials?")) return;
    try {
      await deleteS3Config(token);
      toast.success("S3 configuration removed.");
      await refresh();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Could not remove S3 configuration.");
    }
  };

  return (
    <Layout showSideBar active={2}>
      <Box sx={{ p: { xs: 2, md: 4 }, width: "100%" }}>

        <Grid container alignItems="center" justifyContent="space-between" spacing={2}>
          <Grid item>
            <Typography variant="h5">Media Library</Typography>
            <Typography variant="body2" color="text.secondary">
              Every video you've uploaded or recorded, with its current storage
              location and processing status.
            </Typography>
          </Grid>
          <Grid item>
            <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel>Show</InputLabel>
                <Select
                  label="Show"
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="server">Server only</MenuItem>
                  <MenuItem value="s3">S3 only</MenuItem>
                  <MenuItem value="both">Both</MenuItem>
                  <MenuItem value="missing">Missing</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel>Prefer source</InputLabel>
                <Select
                  label="Prefer source"
                  value={prefer}
                  onChange={(e) => setPrefer(e.target.value)}
                >
                  <MenuItem value="server">Server (S3 fallback)</MenuItem>
                  <MenuItem value="s3">S3 (server fallback)</MenuItem>
                </Select>
              </FormControl>
              <Tooltip title="Refresh">
                <IconButton onClick={refresh} disabled={loading}>
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
              <Button
                variant="outlined"
                startIcon={<SettingsIcon />}
                onClick={() => setConfigOpen(true)}
              >
                S3 Settings
              </Button>
              {/* Direct jump to the Help page – step-by-step S3 setup guide. */}
              <Tooltip title="How to set up S3 / Media tab guide">
                <Button
                  variant="text"
                  size="small"
                  startIcon={<HelpOutlineIcon />}
                  onClick={() => navigate(routerPath.help)}
                >
                  Help
                </Button>
              </Tooltip>
            </Box>

          </Grid>
        </Grid>

        {/* S3 status banner */}
        <Paper sx={{ mt: 3, p: 2, bgcolor: s3Configured ? "success.light" : "warning.light" }}>
          {s3Configured ? (
            <Typography>
              S3 bucket configured: <b>{s3Config?.bucket_name}</b> ({s3Config?.region_name})
              {" — "}
              <Button size="small" onClick={onDeleteConfig} color="error">
                Remove credentials
              </Button>
            </Typography>
          ) : (
            <Typography>
              You haven't configured an S3 bucket yet. Click <b>S3 Settings</b>{" "}
              to add your AWS credentials so you can transfer videos off this
              server.{" "}
              <Button
                size="small"
                variant="text"
                startIcon={<HelpOutlineIcon />}
                onClick={() => navigate(routerPath.help)}
              >
                See setup guide
              </Button>
            </Typography>

          )}
        </Paper>

        {/* Media table */}
        <TableContainer component={Paper} sx={{ mt: 3 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Location</TableCell>
                <TableCell>Size</TableCell>
                <TableCell>Processed?</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              )}
              {!loading && filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography color="text.secondary">No media found.</Typography>
                  </TableCell>
                </TableRow>
              )}
              {!loading &&
                filtered.map((it) => {
                  const key = rowKey(it);
                  const busy = busyRow === key;
                  return (
                    <TableRow key={key} hover>
                      <TableCell>
                        <Typography variant="body2">{it.name || it.filename || "—"}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {it.filename}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={it.type === "uploade" ? "Uploaded" : "Directory"}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={it.storage_location}
                          color={locationChipColor(it.storage_location)}
                        />
                        {it.s3_bucket && (
                          <Tooltip title={`s3://${it.s3_bucket}/${it.s3_key}`}>
                            <Typography variant="caption" sx={{ ml: 1 }} color="text.secondary">
                              {it.s3_bucket}
                            </Typography>
                          </Tooltip>
                        )}
                      </TableCell>
                      <TableCell>{formatBytes(it.size_bytes)}</TableCell>
                      <TableCell>
                        {it.processed ? (
                          <Chip size="small" label="Processed" color="success" />
                        ) : it.processing ? (
                          <Chip size="small" label="Processing…" color="warning" />
                        ) : (
                          <Chip size="small" label="Raw" />
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Play (uses preferred source with fallback)">
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => onPlay(it)}
                              disabled={busy || it.storage_location === "missing"}
                            >
                              <PlayArrowIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Transfer to S3">
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => onTransfer(it, "to_s3")}
                              disabled={busy || !it.can_upload_to_s3}
                            >
                              <CloudUploadIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Pull back from S3 to server">
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => onTransfer(it, "from_s3")}
                              disabled={busy || !it.can_download_from_s3}
                            >
                              <CloudDownloadIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Remove server copy">
                          <span>
                            <IconButton
                              size="small"
                              color="warning"
                              onClick={() => onRemove(it, "server")}
                              disabled={busy || !it.can_delete_server}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Remove S3 copy">
                          <span>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => onRemove(it, "s3")}
                              disabled={busy || !it.can_delete_s3}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      <S3ConfigDialog
        open={configOpen}
        onClose={() => setConfigOpen(false)}
        token={token}
        current={s3Config}
        onSaved={refresh}
      />
    </Layout>
  );
}

export default MediaPage;
